import { useEffect, useRef, useState } from 'react';
import { staffApi, formatApiError, viewProtectedFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtEdDate, fmtEdTime } from '@/lib/tz';
import { toast } from 'sonner';
import { ClipboardList, PlusCircle, Trash2, Download, CheckCircle2, Clock, Paperclip, FileSignature, PenLine, AlertTriangle, FileText } from 'lucide-react';

export const TASK_CATEGORIES = ['Onboarding', 'Training', 'Document', 'Compliance', 'Other'];
export const stamp = (d) => `${fmtEdDate(d, { month: 'short', day: 'numeric', year: 'numeric' })}, ${fmtEdTime(d)} MT`;

const RECUR_OPTS = [
  { v: '', label: 'One-time (no expiry)' },
  { v: '3', label: 'Re-sign every 3 months' },
  { v: '6', label: 'Re-sign every 6 months' },
  { v: '12', label: 'Re-sign every 12 months' },
];

const EMPTY = { title: '', description: '', category: 'Onboarding', dueDate: '', completionType: 'ACKNOWLEDGE', recurMonths: '', signatureMethods: 'BOTH' };
const SIG_METHODS = [
  { v: 'BOTH', label: 'Type or draw (both allowed)' },
  { v: 'TYPED', label: 'Typed name only' },
  { v: 'DRAWN', label: 'Drawn signature only' },
];

// Admin-facing activities/requirements manager for a single staff member (basePath /staff/:id).
export function StaffTasksAdmin({ staffId }) {
  const basePath = `/staff/${staffId}`;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sigView, setSigView] = useState(null);
  const fileRef = useRef(null);

  const load = () =>
    staffApi.get(`${basePath}/tasks`).then(({ data }) => setTasks(data.tasks)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  const submit = async () => {
    if (!f.title.trim()) return toast.error('Enter a title for the activity');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('title', f.title.trim());
      if (f.description.trim()) form.append('description', f.description.trim());
      form.append('category', f.category);
      if (f.dueDate) form.append('dueDate', f.dueDate);
      form.append('completionType', f.completionType);
      if (f.completionType === 'SIGN' && f.recurMonths && f.recurMonths !== 'none') form.append('recurMonths', f.recurMonths);
      if (f.completionType === 'SIGN') form.append('signatureMethods', f.signatureMethods);
      const file = fileRef.current?.files?.[0];
      if (file) form.append('sourceFile', file);
      await staffApi.post(`${basePath}/tasks`, form);
      toast.success('Activity assigned');
      setOpen(false);
      setF(EMPTY);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (taskId) => {
    try {
      await staffApi.delete(`${basePath}/tasks/${taskId}`);
      toast.success('Activity removed');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const viewFile = (path) => viewProtectedFile(staffApi, path).catch((err) => toast.error(formatApiError(err)));

  const statusLine = (t) => {
    if (t.completionType === 'SIGN') {
      if (t.expired) return <span className="text-amber-600 flex items-center gap-1"><AlertTriangle size={12} /> Expired — awaiting re-sign</span>;
      if (t.lastSignature) return <span className="text-coriander">Signed by {t.lastSignature.signerName} · {stamp(t.lastSignature.signedAt)}{t.lastSignature.expiresAt ? ` · expires ${fmtEdDate(t.lastSignature.expiresAt, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}</span>;
      return <span className="text-amber-600">Awaiting signature</span>;
    }
    return <span className={t.status === 'COMPLETED' ? 'text-coriander' : 'text-amber-600'}>{t.status === 'COMPLETED' ? `Completed ${fmtEdDate(t.completedAt, { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Pending'}</span>;
  };

  const typeIcon = (t) => {
    if (t.completionType === 'SIGN') return <FileSignature size={13} className="text-muted-ink shrink-0" />;
    if (t.completionType === 'FILE' || t.requiresFile) return <Paperclip size={13} className="text-muted-ink shrink-0" />;
    return null;
  };

  return (
    <div className="bg-white rounded-xl border border-border/40 p-5" data-testid="staff-tasks-admin">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-700 text-charcoal flex items-center gap-2">
          <ClipboardList size={16} className="text-saffron" /> Activities, Courses &amp; Sign-offs
        </h2>
        <Button data-testid="task-create-open-btn" size="sm" onClick={() => setOpen(true)} className="bg-saffron hover:bg-saffron-hover gap-1.5">
          <PlusCircle size={15} /> Assign
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-ink py-2">Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-muted-ink py-2">No activities assigned yet. Assign onboarding steps, documents to upload, or courses/tests to read &amp; sign.</p>
      ) : (
        <div className="divide-y divide-border/30">
          {tasks.map((t) => (
            <div key={t.id} className="py-3 flex items-start justify-between gap-3" data-testid="task-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {t.status === 'COMPLETED' && !t.expired ? (
                    <CheckCircle2 size={15} className="text-coriander shrink-0" />
                  ) : (
                    <Clock size={15} className="text-amber-500 shrink-0" />
                  )}
                  <p className="text-sm font-medium text-charcoal truncate">{t.title}</p>
                  {typeIcon(t)}
                </div>
                {t.description && <p className="text-xs text-muted-ink mt-0.5">{t.description}</p>}
                <p className="text-xs text-muted-ink mt-0.5">
                  {t.category ? `${t.category} · ` : ''}
                  {t.dueDate ? `Due ${fmtEdDate(t.dueDate, { month: 'short', day: 'numeric' })} · ` : ''}
                  {statusLine(t)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {t.hasSource && (
                  <Button variant="ghost" size="sm" data-testid="task-source-btn" onClick={() => viewFile(`/staff/tasks/${t.id}/source`)} className="text-coriander gap-1">
                    <FileText size={15} /> Doc
                  </Button>
                )}
                {t.lastSignature && (
                  <Button variant="ghost" size="sm" data-testid="task-view-signature-btn" onClick={() => setSigView({ task: t, sig: t.lastSignature })} className="text-coriander gap-1">
                    <PenLine size={15} /> Signature
                  </Button>
                )}
                {t.hasCompletionFile && (
                  <Button variant="ghost" size="sm" data-testid="task-download-btn" onClick={() => viewFile(`/staff/tasks/${t.id}/download`)} className="text-coriander gap-1">
                    <Download size={15} /> File
                  </Button>
                )}
                <Button variant="ghost" size="sm" data-testid="task-delete-btn" onClick={() => remove(t.id)} className="text-destructive">
                  <Trash2 size={15} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-create-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign an Activity</DialogTitle>
            <DialogDescription>Assign a task, a document to upload, or a course/test to read &amp; sign.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="task-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Food Safety Course 2026" />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea data-testid="task-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Any instructions for the staff member…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="task-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Due date (optional)</Label>
                <Input type="date" data-testid="task-due-input" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>How is it completed?</Label>
              <Select value={f.completionType} onValueChange={(v) => setF({ ...f, completionType: v })}>
                <SelectTrigger data-testid="task-completion-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACKNOWLEDGE">One-tap acknowledge</SelectItem>
                  <SelectItem value="FILE">Upload a file (e.g. a certificate)</SelectItem>
                  <SelectItem value="SIGN">Read &amp; sign</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.completionType === 'SIGN' && (
              <div className="space-y-1.5">
                <Label>Re-signing</Label>
                <Select value={f.recurMonths || ''} onValueChange={(v) => setF({ ...f, recurMonths: v })}>
                  <SelectTrigger data-testid="task-recur-select"><SelectValue placeholder="One-time (no expiry)" /></SelectTrigger>
                  <SelectContent>{RECUR_OPTS.map((o) => <SelectItem key={o.v || 'none'} value={o.v || 'none'}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {f.completionType === 'SIGN' && (
              <div className="space-y-1.5">
                <Label>Signature method</Label>
                <Select value={f.signatureMethods} onValueChange={(v) => setF({ ...f, signatureMethods: v })}>
                  <SelectTrigger data-testid="task-sigmethod-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIG_METHODS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{f.completionType === 'SIGN' ? 'Document to read before signing (optional)' : 'Attach a document (optional)'}</Label>
              <input
                ref={fileRef}
                type="file"
                data-testid="task-source-file-input"
                className="block w-full text-sm text-charcoal file:mr-3 file:rounded-lg file:border-0 file:bg-saffron file:px-3 file:py-1.5 file:text-white file:text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="task-create-submit-btn" disabled={busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
              {busy ? 'Assigning…' : 'Assign Activity'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sigView} onOpenChange={(o) => !o && setSigView(null)}>
        <DialogContent data-testid="task-signature-dialog">
          <DialogHeader>
            <DialogTitle>Signature certificate</DialogTitle>
            <DialogDescription>{sigView?.task?.title}</DialogDescription>
          </DialogHeader>
          {sigView && (
            <div className="space-y-3 text-sm">
              {sigView.sig.method === 'DRAWN' && sigView.sig.hasImage && (
                <img
                  data-testid="signature-image"
                  alt="Signature"
                  className="w-full rounded-lg border border-border/50 bg-white"
                  src=""
                  ref={(el) => {
                    if (el && !el.dataset.loaded) {
                      el.dataset.loaded = '1';
                      viewProtectedFile(staffApi, `/staff/tasks/${sigView.task.id}/signatures/${sigView.sig.id}`, { returnUrl: true })
                        .then((url) => { if (url) el.src = url; })
                        .catch(() => {});
                    }
                  }}
                />
              )}
              {sigView.sig.method === 'TYPED' && (
                <p className="font-[cursive] text-2xl text-charcoal border-b border-border/50 pb-2" data-testid="signature-typed">{sigView.sig.signerName}</p>
              )}
              <div className="text-muted-ink space-y-1">
                <p><span className="text-charcoal font-medium">Signed by:</span> {sigView.sig.signerName}</p>
                <p><span className="text-charcoal font-medium">Date &amp; time:</span> {stamp(sigView.sig.signedAt)}</p>
                <p><span className="text-charcoal font-medium">Method:</span> {sigView.sig.method === 'DRAWN' ? 'Drawn signature' : 'Typed signature'}</p>
                {sigView.sig.expiresAt && <p><span className="text-charcoal font-medium">Valid until:</span> {stamp(sigView.sig.expiresAt)}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
